import { FlexibleConnectedPositionStrategyOrigin } from '@angular/cdk/overlay';
import { Component, computed, inject, input, output } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { MedicalComponentSearchPopoverComponent } from 'guideline/feature-component-search';
import { produce } from 'immer';
import { map } from 'rxjs';
import { ComponentConverter, GuidelineService } from 'shared/domain';
import { ButtonDirective, IconDirective, PopoverService } from 'shared/ui-common';
import { isTruthy, OrganisationSettingsService } from 'shared/util-common';

import { FlowchartStartNodeDto, GuidelineElementSection, GuidelineElementType, LinkElementType } from '@treetop/shared/model';

import { CustomisableFlowchartElementComponent } from '../../../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/customisable-flowchart-element/customisable-flowchart-element.component';
import { FlowchartNodeConfig } from '../../../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/flowchart-customisations-settings-tab.component';
import { DividerComponent } from '../../../../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { TitleDescriptionComponent } from '../../../../chapter-sidebar-wrapper/components/title-description/title-description.component';
import {
  GuidelineSidebarBasics,
  GuidelineSidebarBasicsComponent,
} from '../../../../guideline-sidebar-basics/guideline-sidebar-basics.component';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    TitleDescriptionComponent,
    MatButtonModule,

    MatIconModule,
    IconDirective,
    GuidelineSidebarBasicsComponent,
    DividerComponent,
    ButtonDirective,
    CustomisableFlowchartElementComponent,
  ],
  selector: 'sidebar-action-tab',
  templateUrl: './action-tab.component.html',
  styleUrls: ['./action-tab.component.scss'],
})
export class ActionTabComponent {
  private componentConverter = inject(ComponentConverter);
  private popoverService = inject(PopoverService);
  protected organisationSettingsService = inject(OrganisationSettingsService);
  private guidelineService = inject(GuidelineService);

  public sidebarElement = input.required<FlowchartStartNodeDto>();

  public flowchartElementDataChanged = output<Partial<FlowchartStartNodeDto>>();

  protected organisationSettings = computed(
    () => this.guidelineService.guidelineData()?.organisation?.organisationSettings ?? this.organisationSettingsService.settings(),
  );

  protected colorPresets = computed(() => {
    return this.organisationSettings().customisations?.colorPresets ?? [];
  });

  protected hasColorPresets = computed(() => {
    return this.colorPresets().length > 0;
  });

  protected flowchartNodeConfig = computed<FlowchartNodeConfig>(() => {
    const colorPresets = this.colorPresets();
    const organisationCustomisations = this.organisationSettings().customisations?.flowchart;
    const elementColorPreset = this.sidebarElement().colorPresetId;

    const colorPreset = colorPresets.find((preset) => preset.id === (elementColorPreset ?? organisationCustomisations?.start));

    return {
      type: 'start',
      content: 'Start',
      colorPreset,
    };
  });

  protected renderDescription = true;
  protected showSearch = false;
  protected component = new FormControl();

  protected enableSearch($event: MouseEvent) {
    this.showSearch = true;
    if (!this.sidebarElement().guidelineCriteria) {
      this.openSearchDialog({
        x: $event.clientX,
        y: $event.clientY,
      });
    }
  }

  protected onTitleChanged(title: string): void {
    this.flowchartElementDataChanged.emit({ guidelineAction: { title } });
  }

  protected onColorPresetChanged(colorPresetId: string): void {
    this.flowchartElementDataChanged.emit({ colorPresetId });
  }

  protected onDescriptionChangedEvent(description: string): void {
    this.flowchartElementDataChanged.emit({ guidelineAction: { description } });
  }

  protected onShowDescriptionChange(showDescription: boolean): void {
    this.flowchartElementDataChanged.emit({ showDescription });
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    const newComponent = produce({ ...this.sidebarElement() }, (draft) => {
      draft.references = basics.references;
      draft.links = basics.links;
      draft.attachments = basics.attachments;
      draft.resourceTags = basics.resourceTags;
    });

    this.flowchartElementDataChanged.emit(newComponent);
  }

  protected openSearchDialog(point: FlexibleConnectedPositionStrategyOrigin) {
    this.popoverService
      .open(MedicalComponentSearchPopoverComponent, point, {
        width: 400,
        hasBackdrop: true,
        data: {
          definition: this.component,
          findingFilterActive: true,
          activityFilterActive: false,
          observationFilterActive: false,
          enableMultiSearch: true,
          elementType: this.sidebarElement().elementType,
          isFilterSearch: false,
          allowProposedComponents: false,
        },
      })
      .afterClosed()
      .pipe(
        map((result) => result.value),
        isTruthy(),
        map((component) => this.componentConverter.convertFindingToCriterion(component)),
      )
      .subscribe((component) => {
        this.flowchartElementDataChanged.emit({
          guidelineCriteria: component,
          guidelineCriteriaId: component.id,
        });
        this.showSearch = false;
      });
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
  protected readonly LinkElementType = LinkElementType;
}
